import * as d3 from 'd3';
import { Clone } from 'src/app/shared/utils/clone';

interface MeshHeatMapData {
  totalPathLength: number;
  minLength: number;
  maxLength: number;
  elementsLength: any[];
  linesData: any[];
  segmentLengths: any[];
}

export class Heatmap {
  constructor() {}

  samples(samplePath: SVGPathElement, meshHeatMapData: MeshHeatMapData, index: number): number[][] {
    const {
      totalPathLength,
      minLength: min,
      maxLength: max,
      elementsLength,
      segmentLengths,
    } = meshHeatMapData;
    const currentSegmentStartIndex = segmentLengths[index];
    const currentSegmentEndIndex = segmentLengths[index + 1];
    const scaleForHeatmap = d3.scaleLinear().domain([min, max]).range([0, 1]);
    const scaledHeatmapValues = elementsLength.map((length) => scaleForHeatmap(length));
    let scaledSVGLength = this.getScaledSVGLengths(totalPathLength, elementsLength);
    scaledSVGLength = this.normalizeArray(
      scaledSVGLength.slice(currentSegmentStartIndex, currentSegmentEndIndex + 1),
    );
    return scaledSVGLength.map((distance: number, i: number) => {
      const p = samplePath.getPointAtLength(distance);
      const a = [p.x, p.y];
      /* tslint:disable:no-string-literal */
      a['t'] =
        scaledHeatmapValues[currentSegmentStartIndex + i] ||
        scaledHeatmapValues[currentSegmentStartIndex];
      return a;
    });
  }

  getScaledSVGLengths(totalPathLength: number, elementsLength: any[]): any[] {
    const arrayOfLengthCopy = Clone(elementsLength);
    let lengthAccumulator = 0;
    const scaledSVGLength = arrayOfLengthCopy.map((length) => {
      const result = lengthAccumulator;
      lengthAccumulator += length;
      return result;
    });

    const domain = [0, lengthAccumulator];
    const range = [0, totalPathLength];
    const scaleForPath = d3.scaleLinear().domain(domain).range(range);
    return scaledSVGLength.map((length) => scaleForPath(length));
  }

  quads(points: any[]): any[] {
    return d3.range(points.length - 1).map((i) => {
      const currentPoint = points[i];
      const p0 = points[i - 1] || currentPoint;
      const p2 = points[i + 1] || currentPoint;
      const p3 = points[i + 2] || points[i + 1] || currentPoint;
      const a = [p0, currentPoint, p2, p3];
      /* tslint:disable:no-string-literal */
      a['t'] = (currentPoint.t + p2.t) / 2;
      return a;
    });
  }

  joinLine(p0: number[], p1: number[], p2: number[], p3: number[], width: number): string {
    const u12 = this.getPerpendicularPoints(p1, p2);
    const r = width / 2;
    let a = [p1[0] + u12[0] * r, p1[1] + u12[1] * r];
    let b = [p2[0] + u12[0] * r, p2[1] + u12[1] * r];
    let c = [p2[0] - u12[0] * r, p2[1] - u12[1] * r];
    let d = [p1[0] - u12[0] * r, p1[1] - u12[1] * r];

    if (p0) {
      const u01 = this.getPerpendicularPoints(p0, p1);
      const e = [p1[0] + u01[0] + u12[0], p1[1] + u01[1] + u12[1]];
      a = this.getLineIntersection(p1, e, a, b);
      d = this.getLineIntersection(p1, e, d, c);
    }

    if (p3) {
      const u23 = this.getPerpendicularPoints(p2, p3);
      const e = [p2[0] + u23[0] + u12[0], p2[1] + u23[1] + u12[1]];
      b = this.getLineIntersection(p2, e, a, b);
      c = this.getLineIntersection(p2, e, d, c);
    }

    return `M${a}L${b} ${c} ${d}Z`;
  }

  getLineIntersection(a: number[], b: number[], c: number[], d: number[]): number[] {
    const x1 = c[0];
    const x3 = a[0];
    const x21 = d[0] - x1;
    const x43 = b[0] - x3;
    const y1 = c[1];
    const y3 = a[1];
    const y21 = d[1] - y1;
    const y43 = b[1] - y3;
    const ua = (x43 * (y1 - y3) - y43 * (x1 - x3)) / (y43 * x21 - x43 * y21);
    return [x1 + ua * x21, y1 + ua * y21];
  }

  getPerpendicularPoints(p0: number[], p1: number[]): number[] {
    const u01x = p0[1] - p1[1];
    const u01y = p1[0] - p0[0];
    const u01d = Math.sqrt(u01x * u01x + u01y * u01y);
    return u01d ? [u01x / u01d, u01y / u01d] : [0, 0];
  }

  normalizeArray(array: number[]): number[] {
    if (!array) {
      return array;
    }
    return array.map((x, _, originalArr) => x - originalArr[0]);
  }
}
